import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { toast } from 'sonner';
import { Building2, Users, Layers, Mail, TrendingUp } from 'lucide-react';
import { dashboardApi, type DashboardStats, type SubmissionStats } from '@/lib/api/admin-dashboard';
import {
  adminApi,
  type OrgSummary, type BatchSummary, type AdminUserRow, type TrainerPoolEntry,
} from '@/lib/api/admin';
import { Skeleton } from '@/components/ui/skeleton';
import { formatTimestamp } from '@/lib/date';

export const Route = createFileRoute('/admin/analytics')({
  component: AnalyticsPage,
});

// ---- helpers ----
function toMonthKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function keyToLabel(key: string): string {
  const [y, m] = key.split('-');
  return new Date(+y, +m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

function last12MonthKeys(): string[] {
  const keys: string[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return keys;
}

function batchStatus(b: BatchSummary): 'active' | 'upcoming' | 'ended' | 'draft' {
  const today = new Date().toISOString().split('T')[0];
  if (!b.start_date) return 'draft';
  if (b.start_date > today) return 'upcoming';
  if (b.end_date && b.end_date < today) return 'ended';
  return 'active';
}

const C = {
  orange: 'hsl(22, 93%, 56%)',
  blue: '#60a5fa',
  green: '#34d399',
  violet: '#8b5cf6',
  amber: '#fbbf24',
  muted: '#94a3b8',
};

// ---- shared card wrapper ----
function ChartCard({ title, subtitle, children }: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border-border border bg-card shadow-sm">
      <div className="px-5 pt-5 pb-3">
        <h3 className="text-[13px] font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="mt-0.5 text-[11px] text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="px-5 pb-5">{children}</div>
    </div>
  );
}

function KpiCard({ label, value, delta, deltaLabel, icon, color, iconBg }: {
  label: string;
  value: number | string;
  delta?: number;
  deltaLabel?: string;
  icon: React.ReactNode;
  color: string;
  iconBg: string;
}) {
  return (
    <div className="rounded-xl border-border border bg-card p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2.5">
        <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${iconBg}`}>
          {icon}
        </div>
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">
          {label}
        </span>
      </div>
      <div className="flex items-end gap-2">
        <span className={`text-4xl font-bold leading-none ${color}`}>{value}</span>
        {delta !== undefined && delta > 0 && deltaLabel && (
          <div className="mb-0.5 flex items-center gap-1 text-[11px] font-medium text-emerald-500">
            <TrendingUp className="h-3 w-3" />
            <span>+{delta} {deltaLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function RechartsTooltip({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-md text-[12px]">
      {label && <p className="mb-1 font-semibold text-foreground">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color ?? p.fill }}>
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

// ---- main page ----
function AnalyticsPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [trainers, setTrainers] = useState<TrainerPoolEntry[]>([]);
  const [submissionStats, setSubmissionStats] = useState<SubmissionStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      dashboardApi.getStats(),
      adminApi.listOrgs(),
      adminApi.listBatches(),
      adminApi.listUsers(),
      adminApi.listTrainerPool(),
      dashboardApi.getSubmissionStats(),
    ])
      .then(([s, o, b, u, t, ss]) => {
        setStats(s); setOrgs(o); setBatches(b); setUsers(u); setTrainers(t);
        setSubmissionStats(ss);
      })
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  // org creation by month (last 12)
  const orgGrowthData = useMemo(() => {
    const keys = last12MonthKeys();
    const counts: Record<string, number> = Object.fromEntries(keys.map(k => [k, 0]));
    orgs.forEach(o => { const k = toMonthKey(o.created_at); if (k in counts) counts[k]++; });
    return keys.map(k => ({ month: keyToLabel(k), 'New orgs': counts[k] }));
  }, [orgs]);

  // user signup by month (last 12, capped at most-recent 200)
  const userGrowthData = useMemo(() => {
    const keys = last12MonthKeys();
    const counts: Record<string, number> = Object.fromEntries(keys.map(k => [k, 0]));
    users.forEach(u => { const k = toMonthKey(u.created_at); if (k in counts) counts[k]++; });
    return keys.map(k => ({ month: keyToLabel(k), 'New users': counts[k] }));
  }, [users]);

  // batch status donut
  const batchStatusData = useMemo(() => {
    const counts = { active: 0, upcoming: 0, ended: 0, draft: 0 };
    batches.forEach(b => { counts[batchStatus(b)]++; });
    return [
      { name: 'Active',    value: counts.active,   color: C.green },
      { name: 'Upcoming',  value: counts.upcoming,  color: C.blue },
      { name: 'Ended',     value: counts.ended,     color: C.muted },
      { name: 'Draft',     value: counts.draft,     color: C.amber },
    ].filter(d => d.value > 0);
  }, [batches]);

  // user role breakdown
  const userRoleData = useMemo(() => {
    const counts: Record<string, number> = {};
    users.forEach(u => { counts[u.role] = (counts[u.role] ?? 0) + 1; });
    const fmt = (r: string) =>
      r === 'org_admin' ? 'Org Admin' :
      r === 'super_admin' ? 'Super Admin' :
      r.charAt(0).toUpperCase() + r.slice(1);
    return Object.entries(counts)
      .map(([role, count]) => ({ role: fmt(role), count }))
      .sort((a, b) => b.count - a.count);
  }, [users]);

  // top 8 orgs by batch count
  const orgActivityData = useMemo(() =>
    [...orgs]
      .sort((a, b) => b.batch_count - a.batch_count)
      .slice(0, 8)
      .map(o => ({
        name: o.name.length > 16 ? o.name.slice(0, 14) + '…' : o.name,
        Batches: o.batch_count,
        Members: o.member_count,
      })),
    [orgs],
  );

  // submission charts
  const verdictColors: Record<string, string> = {
    ACCEPTED: C.green,
    WRONG_ANSWER: C.blue,
    RUNTIME_ERROR: C.amber,
    TIME_LIMIT_EXCEEDED: C.orange,
    MEMORY_LIMIT_EXCEEDED: C.violet,
    COMPILATION_ERROR: '#f87171',
    PENDING: C.muted,
  };

  const verdictLabel: Record<string, string> = {
    ACCEPTED: 'Accepted',
    WRONG_ANSWER: 'Wrong Answer',
    RUNTIME_ERROR: 'Runtime Error',
    TIME_LIMIT_EXCEEDED: 'TLE',
    MEMORY_LIMIT_EXCEEDED: 'MLE',
    COMPILATION_ERROR: 'Compile Error',
    PENDING: 'Pending',
  };

  const submissionVerdictData = useMemo(() =>
    (submissionStats?.by_verdict ?? []).map(v => ({
      name: verdictLabel[v.verdict] ?? v.verdict,
      count: v.count,
      color: verdictColors[v.verdict] ?? C.muted,
    })),
    [submissionStats],
  );

  const submissionWeekData = useMemo(() =>
    (submissionStats?.by_week ?? []).map(w => {
      const d = new Date(w.period);
      return {
        week: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        Submissions: w.count,
      };
    }),
    [submissionStats],
  );

  // trainer load
  const trainerLoadData = useMemo(() =>
    [...trainers]
      .sort((a, b) => b.active_assignments - a.active_assignments)
      .slice(0, 8)
      .map(t => ({
        name: t.username.length > 13 ? t.username.slice(0, 11) + '…' : t.username,
        'Active batches': t.active_assignments,
      })),
    [trainers],
  );

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-[110px] rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-[280px] rounded-xl" />)}
        </div>
      </div>
    );
  }

  const totalUsers = stats?.total_users ?? 0;
  const sampledNote = totalUsers > 200 ? ` (showing most recent 200 of ${totalUsers})` : '';

  return (
    <div className="space-y-5">

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          label="Organizations"
          value={stats?.total_orgs ?? 0}
          delta={stats?.orgs_delta_30d}
          deltaLabel="this month"
          icon={<Building2 className="h-[18px] w-[18px] text-orange-500" />}
          color="text-orange-500"
          iconBg="bg-orange-50"
        />
        <KpiCard
          label="Users"
          value={stats?.total_users ?? 0}
          delta={stats?.users_delta_7d}
          deltaLabel="this week"
          icon={<Users className="h-[18px] w-[18px] text-blue-500" />}
          color="text-blue-500"
          iconBg="bg-blue-50"
        />
        <KpiCard
          label="Active Batches"
          value={stats?.active_batches ?? 0}
          icon={<Layers className="h-[18px] w-[18px] text-emerald-600" />}
          color="text-emerald-600"
          iconBg="bg-emerald-50"
        />
        <KpiCard
          label="Pending Invites"
          value={stats?.pending_invites ?? 0}
          icon={<Mail className="h-[18px] w-[18px] text-violet-700" />}
          color="text-violet-700"
          iconBg="bg-violet-50"
        />
      </div>

      {/* Growth trends */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Organization growth" subtitle="New organizations per month — last 12 months">
          <ResponsiveContainer width="100%" height={196}>
            <BarChart data={orgGrowthData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F4" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10.5, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10.5, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<RechartsTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
              <Bar dataKey="New orgs" fill={C.orange} radius={[4, 4, 0, 0]} maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="User signups"
          subtitle={`New users per month — last 12 months${sampledNote}`}
        >
          <ResponsiveContainer width="100%" height={196}>
            <LineChart data={userGrowthData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F4" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10.5, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10.5, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<RechartsTooltip />} />
              <Line
                dataKey="New users"
                stroke={C.blue}
                strokeWidth={2}
                dot={{ r: 3, fill: C.blue, strokeWidth: 0 }}
                activeDot={{ r: 5, strokeWidth: 0 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Batch + role breakdown */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Batch status" subtitle={`${batches.length} total batches`}>
          {batchStatusData.length === 0 ? (
            <div className="flex h-[196px] items-center justify-center text-[13px] text-muted-foreground">
              No batches yet
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="55%" height={180}>
                <PieChart>
                  <Pie
                    data={batchStatusData}
                    cx="50%" cy="50%"
                    innerRadius={52} outerRadius={76}
                    paddingAngle={3}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {batchStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<RechartsTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-col gap-3">
                {batchStatusData.map(d => (
                  <div key={d.name} className="flex items-center gap-2 text-[12.5px]">
                    <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="font-semibold text-foreground tabular-nums">{d.value}</span>
                    <span className="text-muted-foreground">{d.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Users by role" subtitle="Distribution across all roles">
          {userRoleData.length === 0 ? (
            <div className="flex h-[196px] items-center justify-center text-[13px] text-muted-foreground">
              No users yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={196}>
              <BarChart data={userRoleData} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F4" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10.5, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="role" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={72} />
                <Tooltip content={<RechartsTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar dataKey="count" name="Users" fill={C.violet} radius={[0, 4, 4, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Org activity + trainer load */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Organization activity" subtitle="Top 8 orgs by batch count">
          {orgActivityData.length === 0 ? (
            <div className="flex h-[196px] items-center justify-center text-[13px] text-muted-foreground">
              No organizations yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={196}>
              <BarChart data={orgActivityData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F4" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10.5, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<RechartsTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar dataKey="Batches" fill={C.orange} radius={[4, 4, 0, 0]} maxBarSize={20} />
                <Bar dataKey="Members" fill={C.blue} radius={[4, 4, 0, 0]} maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Trainer load" subtitle="Active batch assignments per trainer">
          {trainerLoadData.length === 0 ? (
            <div className="flex h-[196px] items-center justify-center text-[13px] text-muted-foreground">
              No trainers in pool yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={196}>
              <BarChart data={trainerLoadData} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F4" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10.5, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={72} />
                <Tooltip content={<RechartsTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar dataKey="Active batches" fill={C.green} radius={[0, 4, 4, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Orgs table */}
      <ChartCard title="Organizations overview" subtitle="All organizations sorted by member count">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                {['Organization', 'Batches', 'Members', 'Pending', 'Created'].map((h, i) => (
                  <th key={h} className={`pb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${i === 0 ? 'text-left' : 'text-right'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {orgs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-[13px] text-muted-foreground">
                    No organizations yet.{' '}
                    <Link to="/admin/orgs/new" className="text-primary hover:underline">Create one</Link>
                  </td>
                </tr>
              ) : (
                [...orgs]
                  .sort((a, b) => b.member_count - a.member_count)
                  .map(o => (
                    <tr key={o.id} className="hover:bg-muted/20">
                      <td className="py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-orange-50">
                            <Building2 className="h-3 w-3 text-orange-500" />
                          </div>
                          <Link
                            to="/admin/orgs/$orgId"
                            params={{ orgId: o.id }}
                            className="text-[13px] font-medium text-foreground hover:text-primary"
                          >
                            {o.name}
                          </Link>
                        </div>
                      </td>
                      <td className="py-3 text-right text-[13px] tabular-nums text-muted-foreground">{o.batch_count}</td>
                      <td className="py-3 text-right text-[13px] tabular-nums text-muted-foreground">{o.member_count}</td>
                      <td className="py-3 text-right">
                        {o.pending_count > 0
                          ? <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{o.pending_count}</span>
                          : <span className="text-[12px] text-muted-foreground">—</span>}
                      </td>
                      <td className="py-3 text-right text-[12px] tabular-nums text-muted-foreground">
                        {formatTimestamp(o.created_at)}
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Submission trends */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard
          title="Submission volume"
          subtitle={`Weekly submissions — last 12 weeks · ${submissionStats?.total ?? 0} total`}
        >
          {submissionWeekData.length === 0 ? (
            <div className="flex h-[196px] items-center justify-center text-[13px] text-muted-foreground">
              No submissions yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={196}>
              <BarChart data={submissionWeekData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F4" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 10.5, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10.5, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<RechartsTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar dataKey="Submissions" fill={C.orange} radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Verdict breakdown" subtitle="Last 90 days">
          {submissionVerdictData.length === 0 ? (
            <div className="flex h-[196px] items-center justify-center text-[13px] text-muted-foreground">
              No submissions yet
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="55%" height={180}>
                <PieChart>
                  <Pie
                    data={submissionVerdictData}
                    dataKey="count"
                    nameKey="name"
                    cx="50%" cy="50%"
                    innerRadius={50} outerRadius={74}
                    paddingAngle={3}
                    strokeWidth={0}
                  >
                    {submissionVerdictData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<RechartsTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-col gap-2.5 min-w-0">
                {submissionVerdictData.map(d => (
                  <div key={d.name} className="flex items-center gap-2 text-[11.5px]">
                    <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="font-semibold text-foreground tabular-nums">{d.count}</span>
                    <span className="text-muted-foreground truncate">{d.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>
      </div>

    </div>
  );
}
